import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Snackbar,
} from '@mui/material';

type Severity = 'success' | 'info' | 'warning' | 'error';

interface ToastOptions {
  message: string;
  severity?: Severity;
  duration?: number;
}

interface ConfirmOptions {
  title?: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
}

interface AlertOptions {
  title?: string;
  message: string;
  severity?: Severity;
  okLabel?: string;
}

interface FeedbackContextValue {
  toast: (opts: ToastOptions | string) => void;
  confirm: (opts: ConfirmOptions) => Promise<boolean>;
  alert: (opts: AlertOptions | string) => Promise<void>;
}

const FeedbackContext = createContext<FeedbackContextValue | undefined>(undefined);

interface ToastState extends ToastOptions {
  open: boolean;
  key: number;
}

interface ConfirmState extends ConfirmOptions {
  open: boolean;
  resolve?: (value: boolean) => void;
}

interface AlertState extends AlertOptions {
  open: boolean;
  resolve?: () => void;
}

export function FeedbackProvider({ children }: { children: ReactNode }) {
  const [toastState, setToastState] = useState<ToastState>({
    open: false,
    message: '',
    severity: 'info',
    key: 0,
  });
  const [confirmState, setConfirmState] = useState<ConfirmState>({ open: false, message: '' });
  const [alertState, setAlertState] = useState<AlertState>({ open: false, message: '' });

  const toast = useCallback((opts: ToastOptions | string) => {
    const o = typeof opts === 'string' ? { message: opts } : opts;
    setToastState({
      open: true,
      message: o.message,
      severity: o.severity ?? 'info',
      duration: o.duration ?? 4000,
      key: Date.now(),
    });
  }, []);

  const confirm = useCallback((opts: ConfirmOptions) => {
    return new Promise<boolean>((resolve) => {
      setConfirmState({ ...opts, open: true, resolve });
    });
  }, []);

  const alertFn = useCallback((opts: AlertOptions | string) => {
    return new Promise<void>((resolve) => {
      const o = typeof opts === 'string' ? { message: opts } : opts;
      setAlertState({ ...o, open: true, resolve });
    });
  }, []);

  const value = useMemo<FeedbackContextValue>(
    () => ({ toast, confirm, alert: alertFn }),
    [toast, confirm, alertFn],
  );

  const handleConfirm = (result: boolean) => {
    confirmState.resolve?.(result);
    setConfirmState((s) => ({ ...s, open: false, resolve: undefined }));
  };

  const handleAlertClose = () => {
    alertState.resolve?.();
    setAlertState((s) => ({ ...s, open: false, resolve: undefined }));
  };

  return (
    <FeedbackContext.Provider value={value}>
      {children}

      <Snackbar
        key={toastState.key}
        open={toastState.open}
        autoHideDuration={toastState.duration ?? 4000}
        onClose={() => setToastState((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          severity={toastState.severity ?? 'info'}
          variant="filled"
          onClose={() => setToastState((s) => ({ ...s, open: false }))}
        >
          {toastState.message}
        </Alert>
      </Snackbar>

      <Dialog
        open={confirmState.open}
        onClose={() => handleConfirm(false)}
        aria-labelledby="confirm-dialog-title"
        aria-describedby="confirm-dialog-description"
      >
        <DialogTitle id="confirm-dialog-title">{confirmState.title ?? 'Are you sure?'}</DialogTitle>
        <DialogContent>
          <DialogContentText id="confirm-dialog-description">{confirmState.message}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => handleConfirm(false)} aria-label="Cancel">
            {confirmState.cancelLabel ?? 'Cancel'}
          </Button>
          <Button
            onClick={() => handleConfirm(true)}
            color={confirmState.destructive ? 'error' : 'primary'}
            variant="contained"
            aria-label={confirmState.confirmLabel ?? 'Confirm'}
            autoFocus
          >
            {confirmState.confirmLabel ?? 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={alertState.open}
        onClose={handleAlertClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{alertState.title ?? 'Notice'}</DialogTitle>
        <DialogContent>
          <Alert severity={alertState.severity ?? 'info'} sx={{ alignItems: 'center' }}>
            <DialogContentText id="alert-dialog-description" sx={{ color: 'inherit' }}>
              {alertState.message}
            </DialogContentText>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleAlertClose} variant="contained" aria-label="OK" autoFocus>
            {alertState.okLabel ?? 'OK'}
          </Button>
        </DialogActions>
      </Dialog>
    </FeedbackContext.Provider>
  );
}

export function useFeedback(): FeedbackContextValue {
  const ctx = useContext(FeedbackContext);
  if (!ctx) throw new Error('useFeedback must be used within a FeedbackProvider');
  return ctx;
}
